import { useEffect, useRef, useCallback, useState } from "react";

/**
 * Encapsulates camera access, frame capture and live-scan overlay.
 * Call startCamera() to request permissions and attach to a <video ref={videoRef}>.
 * Call capture() to snapshot a JPEG and receive {dataUrl, base64}.
 */
export function useCameraScanner() {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [active, setActive] = useState(false);
  const [error, setError] = useState(null);

  const stop = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setActive(false);
  }, []);

  const startCamera = useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      setActive(true);
      setTimeout(() => {
        if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play(); }
      }, 50);
    } catch (err) {
      setError("camera_denied");
    }
  }, []);

  const capture = useCallback(() => {
    const video = videoRef.current;
    if (!video) return null;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth; canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    const idx = dataUrl.indexOf(",");
    const base64 = idx >= 0 ? dataUrl.slice(idx + 1) : dataUrl;
    stop();
    return { dataUrl, base64 };
  }, [stop]);

  useEffect(() => () => stop(), [stop]);

  return { videoRef, active, error, startCamera, capture, stop };
}
